<template>
  <a-drawer
    :maskClosable="false"
    width="50%"
    :label-col="4"
    :wrapper-col="14"
    :visible="open"
    @close="onClose"
    :destroyOnClose="true"
  >
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item label="区域层级" prop="areaLevel" v-if="!isEdit">
        <a-input :value="getAreaLevelText(form.areaLevel)" disabled />
      </a-form-model-item>
      <a-form-model-item label="区域层级" prop="areaLevel" v-else>
        <a-input :value="getAreaLevelText(form.areaLevel)" disabled />
      </a-form-model-item>
      <a-form-model-item label="选择区域" prop="areaCode" v-if="!isEdit">
        <a-select
          v-model="form.areaCode"
          placeholder="请选择区域"
          style="width: 100%"
          :loading="areaOptionsLoading"
          @change="handleAreaChange"
          allow-clear
        >
          <a-select-option v-for="option in areaOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
        <div v-if="form.areaCode" style="margin-top: 8px">
          <a-tag color="blue">区域编码：{{ form.areaCode }}</a-tag>
          <a-tag color="green" style="margin-left: 8px">区域名称：{{ form.areaName }}</a-tag>
        </div>
        <div v-if="!form.areaLevel" style="color: #999; margin-top: 8px">请先选择区域层级</div>
      </a-form-model-item>
      <a-form-model-item label="父级区域" prop="parentId" v-if="form.areaLevel !== 1 && !isEdit">
        <a-tree-select
          v-model="form.parentId"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="parentAreaTreeData"
          placeholder="请选择父级区域"
          :tree-checkable="false"
          :allow-clear="true"
          style="width: 100%"
        />
      </a-form-model-item>
      <a-form-model-item label="代理租户" prop="agentTenantId" v-if="!isEdit">
        <div class="input_d">
          <a-input v-model="agentTenantName" placeholder="请选择代理租户" disabled :maxLength="50" />
          <a-button type="primary" @click="showAgentSelectModal" style="margin-left: 20px">选择</a-button>
          <a-input v-model="form.agentTenantId" placeholder="请选择" disabled :maxLength="50" v-show="shows" />
        </div>
      </a-form-model-item>
      <a-form-model-item label="备注" prop="remark">
        <a-input
          v-model="form.remark"
          type="textarea"
          :rows="3"
          placeholder="请输入备注"
          :max-length="200"
          show-word-limit
        />
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">保存</a-button>
          <a-button type="dashed" @click="cancel">取消</a-button>
        </a-space>
      </div>
    </a-form-model>
    <!-- 选择代理商抽屉组件 -->
    <AgentSelectDrawer ref="agentSelectDrawer" @close="closeAgentSelect" @confirm="handleAgentConfirm" />
  </a-drawer>
</template>

<script>
import { addArea, editArea, fetchAreaPageList, getAreaDetail } from '@/api/profitSharing/proportionalArea'
import { listTenant } from '@/api/system/tenant'
import areadata from '@/utils/areadata.js'
import AgentSelectDrawer from './AgentSelectDrawer.vue'

export default {
  name: 'FormDrawer',
  components: {
    AgentSelectDrawer,
  },
  props: {
    areaList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      submitLoading: false,
      formTitle: '新增维修区域',
      isEdit: false,
      parentAreaTreeData: [],
      treeData: [],
      parentAreaCode: null, // 父级区域编码，用于过滤选项
      areaOptions: [], // 区域选项列表
      areaOptionsLoading: false, // 区域选项加载状态
      shows: false, // 隐藏的输入框显示控制
      agentTenantName: '', // 代理租户名称
      form: {
        id: undefined,
        areaCode: '',
        areaName: '',
        areaLevel: undefined,
        parentId: 0,
        agentTenantId: undefined,
        remark: '',
      },
      rules: {
        areaCode: [{ required: true, message: '请选择区域', trigger: 'blur' }],
        areaLevel: [{ required: true, message: '请选择区域层级', trigger: 'change' }],
        agentTenantId: [{ required: true, message: '请选择代理租户', trigger: 'blur' }],
        parentId: [
          {
            validator: (rule, value, callback) => {
              if (this.form.areaLevel !== 1 && (!value || value === 0)) {
                callback(new Error('请选择父级区域'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          },
        ],
      },
    }
  },
  created() {
    this.treeData = areadata
  },
  computed: {
    // 根据列表数据计算可选择的区域层级
    availableAreaLevel() {
      if (!this.areaList || this.areaList.length === 0) {
        // 列表为空，只能选择省
        return 1
      }
      // 检查是否有省级数据
      const hasProvince = this.areaList.some((item) => item.areaLevel === 1)
      if (!hasProvince) {
        // 没有省级数据，只能选择省
        return 1
      }
      // 检查是否有市级数据
      const hasCity = this.areaList.some((item) => item.areaLevel === 2)
      if (!hasCity) {
        // 有省级但没有市级数据，可以选择市
        return 2
      }
      // 有市级数据，可以选择区
      return 3
    },
  },
  watch: {
    // 监听区域层级变化，自动更新选项列表
    'form.areaLevel'() {
      if (this.open && !this.isEdit) {
        this.generateAreaOptions()
      }
    },
    // 监听父级区域编码变化，自动更新选项列表
    parentAreaCode() {
      if (this.open && !this.isEdit) {
        this.generateAreaOptions()
      }
    },
  },
  methods: {
    // 根据区域层级和父级区域编码生成选项列表
    generateAreaOptions() {
      if (!this.form.areaLevel) {
        this.areaOptions = []
        return
      }

      this.areaOptionsLoading = true

      if (this.form.areaLevel === 1) {
        // 省级：只显示所有省
        this.areaOptions = this.treeData.map((province) => ({
          label: province.label,
          value: province.value.substring(0, 2) + '0000',
          areaCode: province.value.substring(0, 2) + '0000',
          areaName: province.label,
        }))
      } else if (this.form.areaLevel === 2) {
        // 市级：如果有父级区域编码，只显示该省下的市
        if (this.parentAreaCode) {
          const provinceCode = this.parentAreaCode.substring(0, 2) + '0000'
          const matchedProvince = this.treeData.find((province) => {
            const provinceCodeFromTree = province.value.substring(0, 2) + '0000'
            return provinceCodeFromTree === provinceCode
          })
          if (matchedProvince && matchedProvince.children) {
            this.areaOptions = matchedProvince.children.map((city) => ({
              label: city.label,
              value: city.value.substring(0, 4) + '00',
              areaCode: city.value.substring(0, 4) + '00',
              areaName: city.label,
            }))
          } else {
            this.areaOptions = []
          }
        } else {
          // 没有父级区域编码，显示所有市
          this.areaOptions = []
          this.treeData.forEach((province) => {
            if (province.children) {
              province.children.forEach((city) => {
                this.areaOptions.push({
                  label: city.label,
                  value: city.value.substring(0, 4) + '00',
                  areaCode: city.value.substring(0, 4) + '00',
                  areaName: city.label,
                })
              })
            }
          })
        }
      } else if (this.form.areaLevel === 3) {
        // 区级：如果有父级区域编码，只显示该市下的区
        if (this.parentAreaCode) {
          const cityCode = this.parentAreaCode.substring(0, 4) + '00'
          let matchedCity = null
          this.treeData.forEach((province) => {
            if (matchedCity) return
            if (province.children) {
              province.children.forEach((city) => {
                if (matchedCity) return
                const cityCodeFromTree = city.value.substring(0, 4) + '00'
                if (cityCodeFromTree === cityCode) {
                  matchedCity = city
                }
              })
            }
          })
          if (matchedCity && matchedCity.children) {
            this.areaOptions = matchedCity.children.map((area) => ({
              label: area.label,
              value: area.value,
              areaCode: area.value,
              areaName: area.label,
            }))
          } else {
            this.areaOptions = []
          }
        } else {
          // 没有父级区域编码，显示所有区
          this.areaOptions = []
          this.treeData.forEach((province) => {
            if (province.children) {
              province.children.forEach((city) => {
                if (city.children) {
                  city.children.forEach((area) => {
                    this.areaOptions.push({
                      label: area.label,
                      value: area.value,
                      areaCode: area.value,
                      areaName: area.label,
                    })
                  })
                }
              })
            }
          })
        }
      }

      this.areaOptionsLoading = false
    },
    getAreaLevelText(areaLevel) {
      const map = {
        1: '省',
        2: '市',
        3: '区',
      }
      return map[areaLevel] || '--'
    },
    init(record, parentRecord) {
      if (record && record.id) {
        // 编辑模式
        this.isEdit = true
        this.formTitle = '编辑维修区域'
        // 编辑时只需要 id、areaLevel、areaName、remark
        this.form = {
          id: record.id,
          areaCode: record.areaCode || '',
          areaName: record.areaName || '',
          areaLevel: record.areaLevel,
          parentId: record.parentId || 0,
          agentTenantId: record.agentTenantId || undefined,
          remark: record.remark || '',
        }
        // 如果有代理租户ID，需要查询名称
        if (record.agentTenantId) {
          this.loadAgentName(record.agentTenantId)
        } else {
          this.agentTenantName = ''
        }
        this.open = true
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.clearValidate()
          }
        })
      } else {
        // 新增模式：根据是否有父级记录设置默认区域层级
        this.isEdit = false
        // 如果有父级记录，设置 parentId 并获取父级详情
        const parentId = parentRecord && parentRecord.id ? parentRecord.id : 0

        // 如果没有父级记录，默认区域层级为省（1）
        // 如果有父级记录，需要查询详情后根据父级层级决定
        let defaultAreaLevel = 1 // 默认是省

        // 如果有父级记录，先根据父级记录的 areaLevel 推断子级层级
        if (parentId && parentId !== 0 && parentRecord.areaLevel) {
          // 父级是省（1），子级是市（2）
          // 父级是市（2），子级是区（3）
          defaultAreaLevel = parentRecord.areaLevel + 1
        }

        this.form = {
          id: undefined,
          areaCode: '',
          areaName: '',
          areaLevel: defaultAreaLevel,
          parentId: parentId,
          agentTenantId: undefined,
          remark: '',
        }
        this.parentAreaCode = null // 重置父级区域编码
        this.areaOptions = [] // 重置区域选项
        this.agentTenantName = '' // 重置代理租户名称
        this.formTitle = '新增维修区域'

        // 如果有父级记录，获取父级详情以获取 areaCode
        if (parentId && parentId !== 0) {
          getAreaDetail(parentId)
            .then((res) => {
              if (res.success && res.data) {
                this.parentAreaCode = res.data.areaCode // 保存父级区域编码
                // 根据父级详情更新区域层级（如果父级详情中的层级与推断的不一致）
                if (res.data.areaLevel) {
                  this.form.areaLevel = res.data.areaLevel + 1
                }
              }
              this.loadParentAreaList()
              this.generateAreaOptions() // 生成区域选项
              this.open = true
              this.$nextTick(() => {
                if (this.$refs.form) {
                  this.$refs.form.clearValidate()
                }
              })
            })
            .catch(() => {
              this.loadParentAreaList()
              this.generateAreaOptions() // 生成区域选项
              this.open = true
              this.$nextTick(() => {
                if (this.$refs.form) {
                  this.$refs.form.clearValidate()
                }
              })
            })
        } else {
          // 没有父级记录，区域层级默认为省（1）
          this.form.areaLevel = 1
          this.loadParentAreaList()
          this.generateAreaOptions() // 生成区域选项
          this.open = true
          this.$nextTick(() => {
            if (this.$refs.form) {
              this.$refs.form.clearValidate()
            }
          })
        }
      }
    },
    // 处理区域选择变化
    handleAreaChange(value) {
      if (!value) {
        this.form.areaCode = ''
        this.form.areaName = ''
        return
      }

      // 从选项列表中找到选中的项
      const selectedOption = this.areaOptions.find((option) => option.value === value)
      if (selectedOption) {
        this.form.areaCode = selectedOption.areaCode
        this.form.areaName = selectedOption.areaName

        // 如果有父级区域，自动设置父级区域ID
        if (this.form.areaLevel !== 1 && this.parentAreaCode) {
          // 根据区域层级计算父级区域编码
          let parentAreaCode = null
          if (this.form.areaLevel === 2) {
            // 市级的父级是省
            parentAreaCode = this.parentAreaCode.substring(0, 2) + '0000'
          } else if (this.form.areaLevel === 3) {
            // 区级的父级是市
            parentAreaCode = this.parentAreaCode.substring(0, 4) + '00'
          }

          // 从 parentAreaTreeData 中查找父级区域的ID（通过区域编码匹配）
          if (parentAreaCode) {
            const parentArea = this.parentAreaTreeData.find((item) => {
              return item.areaCode === parentAreaCode
            })
            if (parentArea) {
              this.form.parentId = parentArea.value
              // 清除父级区域的验证错误
              this.$nextTick(() => {
                if (this.$refs.form) {
                  this.$refs.form.clearValidate('parentId')
                }
              })
            }
          }
        }

        // 清除验证错误
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.clearValidate('areaCode')
          }
        })
      }
    },
    loadParentAreaList() {
      // 根据区域层级加载父级区域列表
      if (this.form.areaLevel === 1) {
        // 省级不需要父级区域
        this.parentAreaTreeData = []
        return
      }
      const level = this.form.areaLevel === 2 ? 1 : this.form.areaLevel === 3 ? 2 : 1
      fetchAreaPageList({ pageNum: 1, pageSize: 1000, areaLevel: level })
        .then((res) => {
          if (res.success && res.data && res.data.records) {
            this.parentAreaTreeData = res.data.records.map((item) => ({
              title: item.areaName,
              value: item.id,
              key: item.id,
              areaCode: item.areaCode, // 保存区域编码，用于匹配
            }))
          }
        })
        .catch(() => {
          this.parentAreaTreeData = []
        })
    },
    onClose() {
      this.open = false
      this.isEdit = false
      this.form = {
        id: undefined,
        areaCode: '',
        areaName: '',
        areaLevel: undefined,
        parentId: 0,
        remark: '',
      }
      this.parentAreaTreeData = []
      this.parentAreaCode = null // 重置父级区域编码
      this.areaOptions = [] // 重置区域选项
      this.agentTenantName = '' // 重置代理租户名称
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },
    // 显示选择代理商抽屉
    showAgentSelectModal() {
      if (this.$refs.agentSelectDrawer) {
        this.$refs.agentSelectDrawer.open(this.form.agentTenantId)
      }
    },
    // 关闭选择代理商抽屉
    closeAgentSelect() {
      // 抽屉关闭事件，可以在这里做一些清理工作
    },
    // 确认选择代理商
    handleAgentConfirm(agent) {
      this.form.agentTenantId = agent.id
      this.agentTenantName = agent.name
      // 清除验证错误
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate('agentTenantId')
        }
      })
    },
    // 根据ID加载代理商名称
    loadAgentName(agentTenantId) {
      if (!agentTenantId) {
        this.agentTenantName = ''
        return
      }
      listTenant({ pageNum: 1, pageSize: 1, type: 2, id: agentTenantId })
        .then((res) => {
          if (res.success && res.data && res.data.records && res.data.records.length > 0) {
            this.agentTenantName = res.data.records[0].name
          } else {
            this.agentTenantName = ''
          }
        })
        .catch(() => {
          this.agentTenantName = ''
        })
    },
    cancel() {
      this.onClose()
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.submitLoading = true
          const submitData = this.isEdit
            ? {
                id: this.form.id,
                areaLevel: this.form.areaLevel,
                areaName: this.form.areaName,
                remark: this.form.remark,
              }
            : this.form
          const submitMethod = this.isEdit ? editArea : addArea
          submitMethod(submitData)
            .then((res) => {
              if (res.success) {
                this.$message.success(this.isEdit ? '编辑成功' : '新增成功')
                this.onClose()
                this.$emit('success')
              } else {
                this.$message.error(res.message || (this.isEdit ? '编辑失败' : '新增失败'))
              }
              this.submitLoading = false
            })
            .catch(() => {
              this.submitLoading = false
            })
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.bottom-control {
  position: absolute;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #e8e8e8;
  padding: 10px 16px;
  text-align: right;
  left: 0;
  background: #fff;
  border-radius: 0 0 4px 4px;
}

.input_d {
  display: flex;
  align-items: center;
}
</style>
